import { Component, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthPageLayoutComponent } from '../../../layout/auth-page-layout/auth-page-layout.component';
import {
  AgeVerificationService,
  AgeVerificationStatus,
} from '../../../shared/services/age-verification.service';
import { AuthService } from '../../../shared/services/auth.service';
import { AuthStateService } from '../../../shared/states/auth.state';

type ViewState = 'checking' | 'ready' | 'redirecting' | 'error';

/**
 * /age-verification — shown to logged-in users who haven't yet passed
 * VerifyMy. Starts (or resumes) a verification session and sends the
 * browser to VerifyMy; the user comes back to /age-verification/callback.
 */
@Component({
  selector: 'app-age-verification',
  standalone: true,
  imports: [RouterModule, AuthPageLayoutComponent],
  templateUrl: './age-verification.html',
})
export class AgeVerification implements OnInit {
  private ageVerification = inject(AgeVerificationService);
  private authService = inject(AuthService);
  private authState = inject(AuthStateService);
  private router = inject(Router);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly state = signal<ViewState>('checking');
  readonly status = signal<AgeVerificationStatus | null>(null);
  readonly errorMessage = signal('');
  readonly signingOut = signal(false);

  ngOnInit(): void {
    if (!this.isBrowser) return;
    this.loadStatus();
  }

  loadStatus(): void {
    this.state.set('checking');
    this.ageVerification.getStatus().subscribe({
      next: status => {
        this.status.set(status);
        if (!status.required) {
          this.goOn();
          return;
        }
        this.state.set('ready');
      },
      error: err => this.showError(err, 'We couldn’t check your verification status.'),
    });
  }

  /** Button label depends on whether an unfinished session can be resumed. */
  get canResume(): boolean {
    return !!this.status()?.session?.canResume;
  }

  get lastOutcome(): 'failed' | 'expired' | null {
    const s = this.status()?.session?.status;
    return s === 'failed' || s === 'expired' ? s : null;
  }

  startVerification(): void {
    if (this.state() === 'redirecting') return;
    this.state.set('redirecting');

    this.ageVerification.start().subscribe({
      next: result => {
        if (result.status === 'approved') {
          // Background email check passed — no interaction needed.
          this.goOn();
          return;
        }
        // Full-page navigation to VerifyMy (not an iframe/popup, which
        // camera-based methods and mobile browsers handle poorly).
        window.location.assign(result.redirectUrl);
      },
      error: err => this.showError(err, 'We couldn’t start age verification. Please try again.'),
    });
  }

  signOut(): void {
    if (this.signingOut()) return;
    this.signingOut.set(true);
    const clientId = this.authState.getClientId();
    this.authService.logout(clientId ?? undefined)
      .pipe(finalize(() => this.signingOut.set(false)))
      .subscribe({
        next: () => this.finishSignOut(),
        error: () => this.finishSignOut(),
      });
  }

  private finishSignOut(): void {
    const redirectUrl = this.authState.getLogoutRedirectUrl();
    this.authState.clearAuth();
    if (this.isBrowser) window.location.href = redirectUrl;
  }

  private goOn(): void {
    this.router.navigateByUrl(this.ageVerification.consumeReturnUrl(), { replaceUrl: true });
  }

  private showError(err: any, fallback: string): void {
    this.errorMessage.set(err?.error?.message || fallback);
    this.state.set('error');
  }
}
